// src/pages/PagesUtilisateurs/DashboardUsers/modules/PendingPaymentsDisplay.jsx

import React, {
  useEffect,
  useState,
} from "react";

import {
  FaCheckCircle,
} from "react-icons/fa";

import { getReglementsByUser } from "../../../../services/achatsServices";
import { getSettings } from "../../../../services/settingsServices";

import DashboardEmptyState from "../ui/DashboardEmptyState";

import {
  formatDateTime,
  formatEuro,
  getPendingPayments,
} from "../utils/dashboardUser.utils";

/* -------------------------------------------------------------------------- */
/*                                   API URL                                  */
/* -------------------------------------------------------------------------- */

const API_URL = String(
  process.env.REACT_APP_API_URL || "",
).replace(/\/+$/, "");

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function toAbsoluteImageUrl(value) {
  if (!value) return "";

  const cleanValue = String(value).trim();

  if (!cleanValue) return "";

  /*
   * URL déjà absolue
   */
  if (
    cleanValue.startsWith("http://") ||
    cleanValue.startsWith("https://") ||
    cleanValue.startsWith("data:") ||
    cleanValue.startsWith("blob:")
  ) {
    return cleanValue;
  }

  /*
   * URL sans protocole
   */
  if (cleanValue.startsWith("//")) {
    return `https:${cleanValue}`;
  }

  /*
   * Upload API avec slash :
   *
   * /uploads/...
   */
  if (cleanValue.startsWith("/uploads/")) {
    return API_URL
      ? `${API_URL}${cleanValue}`
      : cleanValue;
  }

  /*
   * Upload API sans slash :
   *
   * uploads/...
   */
  if (cleanValue.startsWith("uploads/")) {
    return API_URL
      ? `${API_URL}/${cleanValue}`
      : `/${cleanValue}`;
  }

  /*
   * Asset local du frontend :
   *
   * /img/no-image.jpg
   */
  return cleanValue;
}

/* -------------------------------------------------------------------------- */
/*                      Normalisation de texte                               */
/* -------------------------------------------------------------------------- */

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/* -------------------------------------------------------------------------- */
/*               Détection règlement Licence / Cotisation                    */
/* -------------------------------------------------------------------------- */

function isLicenceCotisationPayment(row, article) {
  /*
   * On vérifie plusieurs champs car selon l'origine du règlement,
   * le texte peut se trouver dans Article.nom, message_achat,
   * type_achat, etc.
   */
  const searchableText = [
    article?.nom,
    article?.title,

    row?.message_achat,
    row?.type_achat,
    row?.libelle,
    row?.designation,
  ]
    .filter(Boolean)
    .join(" ");

  const normalized =
    normalizeText(searchableText);

  /*
   * "Règlement Licence-Cotisation"
   *
   * devient après normalisation :
   *
   * "reglement licence-cotisation"
   */
  return (
    normalized.includes(
      "reglement licence-cotisation",
    ) ||
    normalized.includes(
      "reglement licence cotisation",
    ) ||
    normalized.includes(
      "licence-cotisation",
    ) ||
    normalized.includes(
      "licence cotisation",
    )
  );
}

/* -------------------------------------------------------------------------- */
/*                     Extraction objet settings                             */
/* -------------------------------------------------------------------------- */

function extractSettings(data) {
  /*
   * Ton getSettings() peut retourner :
   *
   * [
   *   {
   *      id: 1,
   *      photo_url_default_lic_cot: "..."
   *   }
   * ]
   *
   * ou directement l'objet.
   */

  if (Array.isArray(data)) {
    return data[0] || {};
  }

  if (
    data &&
    typeof data === "object"
  ) {
    return data;
  }

  return {};
}

/* -------------------------------------------------------------------------- */
/*                         PendingPaymentsDisplay                            */
/* -------------------------------------------------------------------------- */

export default function PendingPaymentsDisplay({
  userId,
  onCounts,
}) {
  const [rows, setRows] = useState([]);

  const [settings, setSettings] =
    useState({});

  const [loading, setLoading] =
    useState(true);

  /* ---------------------------------------------------------------------- */
  /* Chargement                                                             */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!userId) return;

      try {
        setLoading(true);

        /*
         * On récupère en parallèle :
         *
         * - les règlements
         * - les paramètres du club
         */
        const [
          reglementsData,
          settingsData,
        ] = await Promise.all([
          getReglementsByUser(
            userId,
          ),

          getSettings().catch(
            (error) => {
              console.warn(
                "Impossible de charger les settings :",
                error,
              );

              return [];
            },
          ),
        ]);

        if (!mounted) return;

        /* -------------------------------------------------------------- */
        /* Règlements en attente                                          */
        /* -------------------------------------------------------------- */

        const pending =
          getPendingPayments(
            reglementsData,
          ).sort(
            (a, b) =>
              new Date(
                a.date_achat || 0,
              ) -
              new Date(
                b.date_achat || 0,
              ),
          );

        setRows(pending);

        /* -------------------------------------------------------------- */
        /* Settings                                                       */
        /* -------------------------------------------------------------- */

        setSettings(
          extractSettings(
            settingsData,
          ),
        );

        /* -------------------------------------------------------------- */
        /* Compteur                                                       */
        /* -------------------------------------------------------------- */

        onCounts?.({
          total: pending.length,
        });
      } catch (error) {
        console.error(
          "Erreur règlements :",
          error,
        );

        if (mounted) {
          setRows([]);

          onCounts?.({
            total: 0,
          });
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [
    userId,
    onCounts,
  ]);

  /* ---------------------------------------------------------------------- */
  /* Loading                                                                */
  /* ---------------------------------------------------------------------- */

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({
          length: 3,
        }).map((_, index) => (
          <div
            key={index}
            className="
              h-20
              animate-pulse
              rounded-2xl
              bg-slate-100

              dark:bg-slate-800
            "
          />
        ))}
      </div>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Aucun règlement                                                        */
  /* ---------------------------------------------------------------------- */

  if (!rows.length) {
    return (
      <DashboardEmptyState
        icon={FaCheckCircle}
        title="Aucun règlement en attente"
        description="Tous vos règlements sont actuellement à jour."
      />
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <div className="space-y-3">
      {rows.map((row) => {
        /* ---------------------------------------------------------------- */
        /* Article                                                          */
        /* ---------------------------------------------------------------- */

        const article =
          row.Article ||
          row.article ||
          null;

        /* ---------------------------------------------------------------- */
        /* Titre                                                            */
        /* ---------------------------------------------------------------- */

        const title =
          article?.nom ||
          article?.title ||
          row.message_achat ||
          row.type_achat ||
          "Règlement";

        /* ---------------------------------------------------------------- */
        /* Est-ce une licence / cotisation ?                                */
        /* ---------------------------------------------------------------- */

        const isLicenceCotisation =
          isLicenceCotisationPayment(
            row,
            article,
          );

        /* ---------------------------------------------------------------- */
        /* Image article                                                    */
        /* ---------------------------------------------------------------- */

        const articleImage =
          article?.photo_url ||
          article?.photoURL ||
          article?.image_url ||
          "";

        /* ---------------------------------------------------------------- */
        /* Image par défaut Licence / Cotisation                            */
        /* ---------------------------------------------------------------- */

        const licenceCotisationImage =
          settings?.photo_url_default_lic_cot ||
          "";

        /* ---------------------------------------------------------------- */
        /* Choix final de l'image                                           */
        /* ---------------------------------------------------------------- */

        const rawImage =
          isLicenceCotisation
            ? licenceCotisationImage
            : articleImage;

        /* ---------------------------------------------------------------- */
        /* Fallback                                                         */
        /* ---------------------------------------------------------------- */

        const imageUrl =
          toAbsoluteImageUrl(
            rawImage,
          ) ||
          "/img/no-image.jpg";

        /* ---------------------------------------------------------------- */
        /* Date                                                             */
        /* ---------------------------------------------------------------- */

        const {
          date,
        } = formatDateTime(
          row.date_achat,
        );

        /* ---------------------------------------------------------------- */
        /* Render ligne                                                     */
        /* ---------------------------------------------------------------- */

        return (
          <article
            key={row.id}
            className="
              flex
              min-w-0
              items-center
              gap-3

              rounded-2xl

              border
              border-slate-200
              border-l-4
              border-l-amber-400

              bg-white
              p-3

              shadow-sm

              sm:gap-4
              sm:p-4

              dark:border-slate-800
              dark:border-l-amber-500
              dark:bg-slate-900
            "
          >
            {/* ---------------------------------------------------------- */}
            {/* Image                                                      */}
            {/* ---------------------------------------------------------- */}

            <div
              className="
                flex
                h-16 w-16
                shrink-0
                items-center
                justify-center

                overflow-hidden
                rounded-xl

                bg-slate-50

                ring-1
                ring-slate-200

                sm:h-20
                sm:w-20

                dark:bg-slate-950
                dark:ring-slate-700
              "
            >
              <img
                src={imageUrl}
                alt={title}
                loading="lazy"
                className="
                  h-full
                  w-full
                  object-cover
                "
                onError={(event) => {
                  if (
                    event.currentTarget
                      .dataset
                      .fallbackApplied ===
                    "true"
                  ) {
                    return;
                  }

                  event.currentTarget
                    .dataset
                    .fallbackApplied =
                    "true";

                  event.currentTarget.src =
                    "/img/no-image.jpg";
                }}
              />
            </div>

            {/* ---------------------------------------------------------- */}
            {/* Informations                                               */}
            {/* ---------------------------------------------------------- */}

            <div className="min-w-0 flex-1">
              <h4
                className="
                  break-words
                  font-bold
                  leading-5
                  text-slate-950

                  dark:text-white
                "
              >
                {title}
              </h4>

              <div
                className="
                  mt-1
                  flex
                  flex-wrap
                  items-center
                  gap-x-2
                  gap-y-1

                  text-xs
                  text-slate-500

                  dark:text-slate-400
                "
              >
                {row.type_achat ? (
                  <span>
                    {row.type_achat}
                  </span>
                ) : null}

                {row.type_achat &&
                date ? (
                  <span
                    className="
                      text-slate-300
                      dark:text-slate-600
                    "
                  >
                    •
                  </span>
                ) : null}

                {date ? (
                  <span>
                    {date}
                  </span>
                ) : null}
              </div>

              {/* Statut */}

              <span
                className="
                  mt-2
                  inline-flex

                  rounded-full

                  bg-amber-50

                  px-2.5
                  py-1

                  text-xs
                  font-bold

                  text-amber-700

                  dark:bg-amber-500/10
                  dark:text-amber-300
                "
              >
                {row.statut_reglement ||
                  "En attente"}
              </span>
            </div>

            {/* ---------------------------------------------------------- */}
            {/* Montant                                                    */}
            {/* ---------------------------------------------------------- */}

            <div className="shrink-0 text-right">
              <p
                className="
                  whitespace-nowrap

                  text-base
                  font-black
                  text-rose-600

                  sm:text-xl

                  dark:text-rose-400
                "
              >
                {formatEuro(
                  row.prix_total,
                )}
              </p>
            </div>
          </article>
        );
      })}
    </div>
  );
}